import { ErrorPageQueryParameters } from './../pages/error-page/error-page.component';
import { Router } from '@angular/router';
import { NavRoutes } from 'src/app/core/constants/nav.routes';
import { ErrorRouter } from './error-router';
import { Injectable } from '@angular/core';
import { AuthService } from 'src/app/auth/auth.service';
import { MatDialogRef } from '@angular/material/dialog';
import { ToastComponent } from '../components/toasts/toast.component';
import { NotificationType } from 'src/openapi-gen/models';

@Injectable({
  providedIn: 'root'
})
export class ErrorResponseHandler {

  private readonly Server_Down: string = "Couldn't contact server, please try again later.";
  private readonly Internal_Error: string = "Something went wrong, please try again later.";
  private readonly User_Not_Found: string = "User not found.";
  private readonly Invalid_Credentials: string = "Incorrect username or password.";
  private readonly Invalid_Pin: string = "Invalid PIN.";
  private readonly Invalid_File_Detected: string = "Invalid file contents.";
  private readonly Deactivated_Account: string = "This account is deactivated. Please contact an administrator.";
  private readonly Locked_Account: string = "This account is locked. Please reset your password.";
  
  constructor(
    private router: Router,
    private errorRouter: ErrorRouter
  ) { }

  /**
    * Affiliated with scenarios involving login functionality
    * @param statusCode  Http request error response code
    * @returns An error messsage
    */
  public handle_login(statusCode: number): string {
    switch (statusCode) {
      case 0:
        return this.Server_Down;
      case 400:
      case 401:
      case 404:
        return this.Invalid_Credentials;
      case 403:
        return this.Deactivated_Account;
      case 423:
        return this.Locked_Account;
      default:
        return this.Internal_Error;
    }
  }

  /**
    * Affiliated with scenarios involving login functionality
    * @param statusCode  Http request error response code
    * @returns An error messsage
    */
  public handle_signup(statusCode: number): string {
    switch (statusCode) {
      case 0:
        return this.Server_Down;
      case 400:
        return this.Invalid_File_Detected;
      default:
        return this.Internal_Error;
    }
  }

  /**
    * Affiliated with scenarios involving login functionality for kiosk pins
    * @param statusCode  Http request error response code
    * @returns An error messsage
    */
  public handle_kiosk_pin_login(statusCode: number): string {
    switch (statusCode) {
      case 0:
        return this.Server_Down; 
      case 403:
        return this.Deactivated_Account;
      case 404:
        return this.Invalid_Pin;
      default:
        return this.Internal_Error;
    }
  }

  /**
    * Affiliated with scenarios involving login functionality for kiosk barcodes
    * @param statusCode  Http request error response code
    * @returns An error messsage
    */
  public handle_kiosk_barcode_login(statusCode: number, toast: ToastComponent) {
    toast.clearToasts();
    switch(statusCode) {
      case 0:
        toast.showToast({ action: NotificationType.Error, message: "Couldn't contact our servers. Please try again later or contact an administrator."});
        break;
      case 403:
        toast.showToast({ action: NotificationType.Warning, message: "This account is deactivated. Please try again or contact an administrator."});
        break;
      case 404:
        toast.showToast({ action: NotificationType.Warning, message: "This account does not exist. Please try again or contact an administrator."});
        break;
      default:
        toast.showToast({ action: NotificationType.Error, message: "Please try again or contact an administrator."});
        break;
    }
  }

  /**
    * Affiliated with scenarios involving logout functionality
    * @param statusCode Http request error response code
    */
  public handle_logout(statusCode: number) {
    switch (statusCode) {
      case 0:
        this.errorRouter.navigateToServerConnectionError();
        break;
      default:
        this.router.navigate([NavRoutes.Root]);
    }
  }

  /**
    * Affiliated with scenarios involving logout functionality for the kiosk
    * @param statusCode Http request error response code
    */
  public handle_kiosk_logout(statusCode: number, dialogRef: MatDialogRef<any>): string {
    switch (statusCode) {
      case 401:
        return this.Invalid_Pin;
      default:
        AuthService.SetUserLoggedOut();
        this.router.navigate([NavRoutes.Root]);
        dialogRef.close(false);
        break;
    }
  }

  /**
    * Affiliated with scenarios involving logout functionality for the kiosk
    * @param statusCode Http request error response code
    */
  public handle_kiosk_logout_alternative(statusCode: number) {
    this.router.navigate([NavRoutes.Kiosk]);
  }

  /**
    * Affiliated with scenarios involving sending a password reset email to a given account
    * @param statusCode Http request error response code
    * @returns An error messsage
    */
  public handle_password_reset_email(statusCode: number): string {
    switch(statusCode) {
      case 0: 
        return this.Server_Down;
      case 404:
        return this.User_Not_Found;
      default: 
        return this.Internal_Error;
    }
  }

  /**
    * Affiliated with scenarios involving verification codes
    * @param queryParams A set of inputs used to customize the display of the error page on a 400 status
    */
  public handle_verification_code(statusCode: number, queryParams: ErrorPageQueryParameters) {
    switch(statusCode) {
      case 0:
        this.errorRouter.navigateToServerConnectionError();
        break;
      case 400:
        this.errorRouter.navigateToCustomError(queryParams);
        break;
      default: 
        this.errorRouter.navigateToInternalError();
    }
  }
}